import { useFormContext } from 'react-hook-form';
import { ProjectData } from '../../types';
import { HelpTooltip } from '../HelpTooltip';
import { normalizeUrl } from '../../utils/urlUtils';

interface ShopConfigurationStepProps {
  appType: 'versirent' | 'idealone';
  invalidFields?: string[];
  onFieldChange?: () => void;
}

export default function ShopConfigurationStep({ appType, invalidFields = [], onFieldChange }: ShopConfigurationStepProps) {
  const { register, watch, setValue, formState: { errors } } = useFormContext<ProjectData['formData']>();
  
  const ecommerceType = watch('ecommerceType');
  const secondaryColor = watch('secondaryColor');
  const secondaryColorSwatch =
    secondaryColor && /^#[0-9A-Fa-f]{6}$/.test(secondaryColor) ? secondaryColor : '#CCCCCC';

  // Kept for call-site compatibility; secondary color is now shown for both apps (IMA-13)
  void appType;
  void invalidFields;
  void onFieldChange;

  return (
    <div className="space-y-6">
      {/* E-commerce Configuration */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🛒 E-commerce Configuration</h3>
        
        {/* E-commerce Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🛒 Choose Your E-commerce Solution *
          </label>
          
          <div className="space-y-3">
            {/* Integrated Shop Option */}
            <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              ecommerceType === 'integrated' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <label className="flex items-start cursor-pointer">
                <input
                  {...register('ecommerceType', { required: 'Please select an e-commerce solution' })}
                  type="radio"
                  value="integrated"
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  onChange={(e) => {
                    register('ecommerceType').onChange(e);
                    onFieldChange?.();
                  }}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">
                    🏪 Integrated Shop
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Use our integrated shop feature
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    ✅ Seamless integration • ✅ First-class inventory integration • ✅ No website needed
                  </div>
                </div>
              </label>
            </div>

            {/* Custom Site in App Option */}
            <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              ecommerceType === 'custom_in_app' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <label className="flex items-start cursor-pointer">
                <input
                  {...register('ecommerceType')}
                  type="radio"
                  value="custom_in_app"
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  onChange={(e) => {
                    register('ecommerceType').onChange(e);
                    onFieldChange?.();
                  }}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">
                    📱 Custom Site in App
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Embed your existing e-commerce site directly within the app
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    ✅ Keep existing website • ✅ Seamless integration
                  </div>
                </div>
              </label>
            </div>

            {/* Custom Site in Browser Option */}
            <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              ecommerceType === 'custom_browser' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <label className="flex items-start cursor-pointer">
                <input
                  {...register('ecommerceType')}
                  type="radio"
                  value="custom_browser"
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  onChange={(e) => {
                    register('ecommerceType').onChange(e);
                    onFieldChange?.();
                  }}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">
                    🌐 Custom Site in Browser
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Redirect customers to your e-commerce website in their default browser
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    ✅ Keep existing website
                  </div>
                </div>
              </label>
            </div>
          </div>

          {errors.ecommerceType && (
            <p className="mt-2 text-sm text-red-600">{errors.ecommerceType.message}</p>
          )}
          
          <HelpTooltip helpText="Choose the e-commerce solution that best fits your business needs. Integrated Shop provides a complete built-in solution, while custom options let you use your existing website." />
        </div>

        {/* Custom Shop URL - only show if using custom e-commerce options */}
        {(ecommerceType === 'custom_in_app' || ecommerceType === 'custom_browser') && (
          <div>
            <label htmlFor="redirectWebsiteUrl" className="block text-sm font-medium text-gray-700 mb-2">
              🌐 Custom Shop URL *
            </label>
            <input
              {...register('redirectWebsiteUrl', {
                required: (ecommerceType === 'custom_in_app' || ecommerceType === 'custom_browser') ? 'Custom Shop URL is required for custom e-commerce options' : false,
                pattern: {
                  value: /^(https?:\/\/.+|www\..+|\w+\..+)/,
                  message: 'Please enter a valid URL (e.g., yourwebsite.com or https://yourwebsite.com)',
                },
                onChange: (e) => {
                  const normalizedUrl = normalizeUrl(e.target.value);
                  if (normalizedUrl !== e.target.value) {
                    setValue('redirectWebsiteUrl', normalizedUrl);
                  }
                  onFieldChange?.();
                },
              })}
              type="url"
              id="redirectWebsiteUrl"
              placeholder="yourwebsite.com/shop or https://yourwebsite.com/shop"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {errors.redirectWebsiteUrl && (
              <p className="mt-1 text-sm text-red-600">{errors.redirectWebsiteUrl.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              💡 Your e-commerce website URL that will be used for shop features
            </p>
            <HelpTooltip helpText="This URL will be used for shop redirects and must be a valid HTTPS URL. Required for custom e-commerce options." />
          </div>
        )}
      </div>

      {/* Secondary Brand Color */}
      <div>
        <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-700 mb-2">
          🎨 Secondary Brand Color
        </label>
        <div className="flex items-center space-x-3">
          <input
            type="color"
            id="secondaryColor"
            value={secondaryColorSwatch}
            className="h-10 w-20 border border-gray-300 rounded-md cursor-pointer"
            onChange={(e) => {
              setValue('secondaryColor', e.target.value, { shouldDirty: true });
              onFieldChange?.();
            }}
          />
          <input
            {...register('secondaryColor')}
            type="text"
            placeholder="#10B981"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onChange={(e) => {
              setValue('secondaryColor', e.target.value, { shouldDirty: true });
              onFieldChange?.();
            }}
          />
        </div>
        <p className="mt-1 text-sm text-gray-500">
          💡 Optional secondary color for additional UI elements
        </p>
        <HelpTooltip helpText="Choose a secondary brand color that complements your primary color. This will be used for secondary UI elements and accents." />
      </div>

      {/* Information Box */}
      <div className="bg-green-50 border border-green-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Shop Configuration
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>
                {ecommerceType === 'integrated' 
                  ? "Your app will use our integrated shop feature with seamless integration and first-class inventory management. No website needed."
                  : ecommerceType === 'custom_in_app'
                  ? "Your app will embed your existing e-commerce site directly within the app, keeping your website while providing seamless integration."
                  : "Your app will redirect customers to your website in their default browser. Make sure your website is mobile-optimized and can handle the expected traffic."
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
